import { createSlice } from '@reduxjs/toolkit';
import { LABELGENAPI } from '../../../API';
import { logFrontendActivity } from '../../General/activityLogger';

export const fulfillmentsLogSlice = createSlice({
    name: 'fulfillmentsLog',
    initialState: {
        data: [],
        topSkus: [], // ─── NEW INITIAL STATE KEY ───
        topSkusLoading: false, // ─── NEW INITIAL STATE KEY ───
        meta: {
            page: 1,
            per_page: 10,
            start_date: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0], 
            end_date: new Date().toISOString().split('T')[0],
            sorting: JSON.stringify([{
                field: 'log_date',
                sort: 'desc',
            }])
        },
        loading: false,
        batchDownloading: false, 
        hasErrors: false,
        notification: { show: false, messageContent: "", messageType: "success" },
        batchPrintDetails: {
            data: null,
            loading: false
        }
    },
    reducers: {
        getFulfillmentsData: (state) => {
            return {
                ...state,
                loading: true,
                hasErrors: false
            }
        },
        getFulfillmentsDataSuccess: (state, action) => {
            return {
                ...state,
                data: action && action.payload && action.payload.data && action.payload.data.data,
                meta: action && action.payload && action.payload.data,
                hasErrors: false,
                loading: false
            }
        },
        getFulfillmentsDataFailure: (state, action) => {
            return {
                ...state,
                loading: false,
                hasErrors: true,
                message: action && action.payload && action.payload.data && action.payload.data.message
            }
        },
        // ─── NEW REDUCERS FOR TOP 5 SKU STATS OVERLAYS ───
        getTopSkusData: (state) => {
            state.topSkusLoading = true;
        },
        getTopSkusDataSuccess: (state, action) => {
            state.topSkusLoading = false;
            state.topSkus = action.payload;
        },
        getTopSkusDataFailure: (state) => {
            state.topSkusLoading = false;
            state.topSkus = [];
        },
        getPrintBatchData: (state) => {
            state.batchPrintDetails.loading = true;
        },
        getPrintBatchDataSuccess: (state, action) => {
            state.batchPrintDetails.loading = false;
            state.batchPrintDetails.data = action.payload; 
        },
        getPrintBatchDataFailure: (state) => {
            state.batchPrintDetails.loading = false;
            state.batchPrintDetails.data = null;
        },
        clearFulfillmentPrintDetails: (state) => {
            state.batchPrintDetails.loading = false;
            state.batchPrintDetails.data = null;
        },
        exportBatchCSVStart: (state) => {
            state.batchDownloading = true;
        },
        exportBatchCSVEnd: (state) => {
            state.batchDownloading = false;
        },
        resetFulfillments: (state) => {
            return {
                ...state,
                loading: false,
                topSkusLoading: false,
                batchDownloading: false,
                topSkus: [],
                hasErrors: false,
                notification: { show: false, messageContent: "", messageType: "success" }
            }
        }
    }
});

export const { 
    getFulfillmentsData, 
    getFulfillmentsDataSuccess, 
    getFulfillmentsDataFailure, 
    getTopSkusData,
    getTopSkusDataSuccess,
    getTopSkusDataFailure,
    exportBatchCSVStart, 
    exportBatchCSVEnd, 
    resetFulfillments 
} = fulfillmentsLogSlice.actions;

export const fetchFulfillmentsData = (meta) => {
    const qs = Object.keys(meta)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(meta[key])}`)
        .join('&');

    return (dispatch) => {
        dispatch(getFulfillmentsData());
        try {
            LABELGENAPI
                .get('/fulfillmentsInfo?' + qs)
                .then(function (response) {
                    dispatch(getFulfillmentsDataSuccess(response));
                })
                .catch(function (error) {
                    dispatch(getFulfillmentsDataFailure(error.response));
                });
        } catch (error) {
            dispatch(getFulfillmentsDataFailure());
        }
    };
};

// ─── NEW ASYNC THUNK ACTION TO FETCH THE TOP FULFILLMENT SKUS OVER THE COMPONENT FILTER WINDOW ───
export const fetchTopFulfillmentSkusData = (meta) => {
    const chartMeta = { start_date: meta.start_date, end_date: meta.end_date };
    const qs = Object.keys(chartMeta)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(chartMeta[key])}`)
        .join('&');

    return (dispatch) => {
        dispatch(getTopSkusData());
        try {
            LABELGENAPI
                .get('/fulfillmentsInfo/top-skus?' + qs)
                .then(function (response) {
                    dispatch(getTopSkusDataSuccess(response.data?.data || []));
                })
                .catch(function () {
                    dispatch(getTopSkusDataFailure());
                });
        } catch (error) {
            dispatch(getTopSkusDataFailure());
        }
    };
};

export const { 
    getPrintBatchData, 
    getPrintBatchDataSuccess, 
    getPrintBatchDataFailure, 
    clearFulfillmentPrintDetails 
} = fulfillmentsLogSlice.actions;

export const loadFulfillmentBatchForPrinting = (userid, logDate) => dispatch => {
    dispatch(getPrintBatchData());
    try {
        LABELGENAPI.get(`/fulfillmentsInfo/print-batch-raw?date=${encodeURIComponent(logDate)}&userid=${userid}`)
            .then(function (response) {
                dispatch(getPrintBatchDataSuccess(response.data));
            })
            .catch(function (error) {
                dispatch(getPrintBatchDataFailure());
            });
    } catch (error) {
        dispatch(getPrintBatchDataFailure());
    }
};

export const exportFulfillmentBatchCSV = (userid, logDate, callback) => dispatch => {
    const logRef = crypto.randomUUID();
    
    dispatch(exportBatchCSVStart());
    
    try {
        LABELGENAPI.get(`/fulfillmentsInfo/export-batch?date=${encodeURIComponent(logDate)}`, { responseType: 'blob' })
            .then(function (response) {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `fulfillment_orders_${logDate}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                
                logFrontendActivity({
                    group: 'REPORTS',
                    subgroup: 'FULFILLMENTS',
                    action: 'EXPORT_BATCH_SUCCESS',
                    request_data: { date: logDate },
                    response_data: { message: "CSV orders payload extracted successfully." },
                    log_ref: logRef,
                    userid: userid
                });

                dispatch(exportBatchCSVEnd());
                if (typeof callback === 'function') {
                    callback();
                }
            })
            .catch(function (error) {
                console.error(error);
                dispatch(exportBatchCSVEnd());
            });
    } catch (error) {
        console.error(error);
        dispatch(exportBatchCSVEnd());
    }
};

export const exportFulfillmentSummaryCSV = (meta) => dispatch => {
    const chartMeta = { start_date: meta.start_date, end_date: meta.end_date };
    const qs = Object.keys(chartMeta)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(chartMeta[key])}`)
        .join('&');

    dispatch(exportBatchCSVStart()); 

    try {
        LABELGENAPI.get(`/fulfillmentsInfo/export-summary?${qs}`, { responseType: 'blob' })
            .then(function (response) {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `fulfillment_summary_report_${new Date().toISOString().split('T')[0]}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                
                dispatch(exportBatchCSVEnd());
            })
            .catch(function (error) {
                console.error(error);
                dispatch(exportBatchCSVEnd());
            });
    } catch (error) {
        console.error(error);
        dispatch(exportBatchCSVEnd());
    }
};

export default fulfillmentsLogSlice.reducer;